export interface SkillNode {
    id: number;
    name: string;
    description: string;
    published: boolean;
    level: number;
    skill_type?: { id: number } | number | null;
    children: SkillNode[];
}

export interface SunburstNode {
    label: string;
    value?: number;
    children?: SunburstNode[];
}

export interface SkillTreeNodeData {
    id: string;
    name: string;
    description?: string;
    skillTypeId?: number | null;
    children?: SkillTreeNodeData[];
}

/**
 * Strip HTML tags from content (for legacy HTML data)
 */
function stripHtmlTags(html: string): string {
    return html.replace(/<[^>]*>/g, '').trim();
}

/**
 * Extract text from Lexical JSON (for legacy JSON data)
 */
function extractTextFromLexicalJson(obj: Record<string, unknown>): string {
    const texts: string[] = [];

    function traverse(node: any) {
        if (!node) return;
        if (node.text && typeof node.text === 'string') {
            texts.push(node.text);
        }
        if (Array.isArray(node.children)) {
            node.children.forEach((child: any) => traverse(child));
        }
    }

    traverse(obj);
    return texts.join('').trim();
}

/**
 * Normalize description to plain text
 * Handles legacy Lexical JSON and HTML formats, returns plain text
 */
export const normalizeSkillDescription = (description?: string | null): string => {
    if (!description) return '';

    const trimmed = description.trim();

    // Try to parse as Lexical JSON (legacy)
    try {
        const parsed = JSON.parse(trimmed) as Record<string, unknown>;
        if (parsed && typeof parsed === 'object' && 'root' in parsed) {
            return extractTextFromLexicalJson(parsed);
        }
    } catch {
        // Not JSON, continue
    }

    // Strip HTML tags if present (legacy)
    if (trimmed.includes('<') && trimmed.includes('>')) {
        return stripHtmlTags(trimmed);
    }

    // Return as plain text
    return trimmed;
};

export const transformDataForSunburst = (skill: SkillNode): SunburstNode => {
    const hasChildren = skill.children && skill.children.length > 0;

    if (hasChildren) {
        return {
            label: skill.name,
            children: skill.children.map(transformDataForSunburst),
        };
    }

    return {
        label: skill.name,
        value: 1,
    };
};

export const transformDataForTree = (skill: SkillNode): SkillTreeNodeData => ({
    id: String(skill.id),
    name: skill.name,
    description: skill.description,
    skillTypeId:
        typeof skill.skill_type === 'object'
            ? skill.skill_type?.id ?? null
            : typeof skill.skill_type === 'number'
              ? skill.skill_type
              : null,
    children: skill.children?.map(transformDataForTree) || [],
});
